// @flow
import moment from 'moment';
import {Models} from '@wellstone-solutions/common';
import type {
  UIMemberBasicType,
  UIMemberType,
} from '@wellstone-solutions/common';
import type {
  V2SessionDocumentation,
  V2SessionDocumentationRequest,
  V2OrgSessionServiceType,
} from 'api/v2';
import {getFullMomentDateTimes} from 'utils/getFullMomentDateTimes';
import type {UISessionDocumentType, UISessionServiceType} from '../model/types';
import {TIME_FORMAT_24} from '../constants';

const {MemberBasic} = Models;

// The V2 serializer references member/staff by Member PK; Bridge resolves them
// through memberStore, which returns a full UIMemberType. Flatten to the
// UIMemberBasicType shape the documentation UI expects (email lives at the top
// level, not under user.email).
export const memberToBasic = (
  member: ?UIMemberType,
): UIMemberBasicType | void =>
  member
    ? MemberBasic.Factory({
        id: member.id,
        name: member.name,
        role: member.role,
        email: member.user?.email ?? '',
        birthdate: member.birthdate,
        integrations: member.integrations ?? [],
        userId: member.user?.id ?? '',
      })
    : undefined;

// org-service-types carry a flat, denormalized `name`, so no catalog lookup is
// needed to render the multi-select.
export const toUIServiceTypes = (
  serviceTypes: Array<V2OrgSessionServiceType>,
): Array<UISessionServiceType> =>
  serviceTypes.map(({id, name}) => ({id, name}));

// V2 write payload — the API accepts bare UUIDs for member/staff/typeOfService
// and camelCase field names, so this is a direct projection of the UI doc with
// the date/time fields normalized to ISO.
export const toApiRequest = (
  sessionDoc: UISessionDocumentType,
): V2SessionDocumentationRequest => {
  const {
    member,
    staff,
    endTime,
    dateOfService,
    typeOfService,
    startTime,
    status,
    isAllDay,
    sessionType,
    location,
    titleOrReason,
    didMemberAttend,
    notes,
    nextSteps,
  } = sessionDoc;

  let start;
  let end;
  if (dateOfService) {
    const momentDates = getFullMomentDateTimes(
      dateOfService,
      startTime,
      endTime,
    );
    start = momentDates.start?.toISOString();
    end = momentDates.end?.toISOString() || '';
  }

  // $FlowIgnoreMe - api-client request type is generated (TS .d.ts)
  return {
    status,
    isAllDay,
    sessionType,
    location,
    titleOrReason,
    didMemberAttend,
    notes,
    nextSteps,
    member: member.id,
    staff: staff.id,
    startTime: start,
    endTime: end,
    dateOfService: dateOfService
      ? moment(dateOfService).format('YYYY-MM-DD')
      : null,
    typeOfService: typeOfService.map(({id}) => id),
  };
};

// Map a raw V2 document to the nested UI shape the form/list/view tree consumes.
// V2 sends member/staff and typeOfService as bare UUIDs, so the caller injects
// the already-resolved member/staff (from memberStore) and the org's service
// types; this joins the typeOfService UUIDs to {id, name} and formats the time
// fields. Missing relations fall back to an id-only object so the row still
// renders.
export const toUISessionDocument = (
  sessionDoc: V2SessionDocumentation,
  {
    member,
    staff,
    serviceTypes = [],
  }: {
    member?: ?UIMemberBasicType,
    staff?: ?UIMemberBasicType,
    serviceTypes?: Array<UISessionServiceType>,
  } = {},
): UISessionDocumentType => {
  const {
    startTime,
    endTime,
    member: memberId,
    staff: staffId,
    typeOfService: typeOfServiceIds,
    ...rest
  } = sessionDoc;

  // 24hr strings are what the form time fields expect.
  const uiStartTime = startTime
    ? moment(startTime).format(TIME_FORMAT_24)
    : startTime;
  const uiEndTime = endTime ? moment(endTime).format(TIME_FORMAT_24) : endTime;

  const serviceTypeLookup = new Map(serviceTypes.map((st) => [st.id, st]));
  const typeOfService = (typeOfServiceIds ?? []).map(
    (id) => serviceTypeLookup.get(id) ?? {id, name: ''},
  );

  // $FlowIgnoreMe - building the UI shape from the generated (TS) response type
  return {
    integrationErrors: [],
    ...rest,
    startTime: uiStartTime,
    endTime: uiEndTime,
    member: member ?? MemberBasic.Factory({id: memberId}),
    staff: staff ?? MemberBasic.Factory({id: staffId}),
    typeOfService,
  };
};
